import guideMarkdown from "@/content/ota-mapping-guide.md?raw";
import { useAuthStore } from "@/stores/auth";
import ReactMarkdown from "react-markdown";
import { Link, Navigate } from "react-router-dom";
import remarkGfm from "remark-gfm";

/** Anchor id from a heading's text — keeps Korean letters, hyphenates the rest. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

const TOC = guideMarkdown
  .split("\n")
  .filter((line) => line.startsWith("## "))
  .map((line) => line.slice(3).trim());

/** Superadmin-only mirror of the internal OTA ↔ Channex mapping guide. */
export function OtaMappingGuidePage() {
  const user = useAuthStore((s) => s.user);

  // Wait for /auth/me before judging; then bounce non-superadmins.
  if (user && !user.is_superadmin) return <Navigate to="/admin" replace />;
  if (!user) return null;

  return (
    <div className="space-y-6">
      <div>
        <p className="eyebrow-brass">Operations</p>
        <h1 className="mt-1 font-display text-2xl font-medium">OTA 채널 매핑 가이드</h1>
        <p className="text-sm text-muted-foreground">
          숙소 승인 후 Channex 대시보드에서 OTA 매핑 시 참조하는 운영팀 내부 문서입니다.
        </p>
        <Link
          to="/admin/super"
          className="mt-2 inline-block text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          ← 슈퍼어드민
        </Link>
      </div>

      <div className="grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <nav
            aria-label="가이드 목차"
            className="sticky top-24 space-y-1 border-l border-border/70 pl-4"
          >
            <p className="eyebrow mb-3">목차</p>
            {TOC.map((title) => (
              <a
                key={title}
                href={`#${slugify(title)}`}
                className="block py-1 text-[13px] leading-snug text-muted-foreground transition-colors hover:text-foreground"
              >
                {title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="doc-prose min-w-0 max-w-3xl">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
              img: ({ src, alt }) => <img src={src} alt={alt ?? ""} loading="lazy" />,
            }}
          >
            {guideMarkdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
